import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  getDoc,
  serverTimestamp,
  orderBy,
  limit,
  deleteDoc
} from 'firebase/firestore';
import { COLLECTIONS } from '../firebase/collections';
import { NotificationService } from './NotificationService';

/**
 * Service pour gérer les feedbacks et évaluations des demandes de travaux
 */
export class FeedbackService {
  /**
   * Crée ou met à jour un feedback pour une demande de travaux
   * @param {Object} feedbackData - Les données du feedback
   * @param {string} feedbackData.workRequestId - ID de la demande de travaux
   * @param {number} feedbackData.rating - Note globale (1-5)
   * @param {string} feedbackData.comment - Commentaire optionnel
   * @param {Object} feedbackData.details - Détails de l'évaluation
   * @param {string} feedbackData.createdBy - ID de l'utilisateur qui crée le feedback
   * @returns {Promise<string>} - ID du feedback créé ou mis à jour
   */
  static async createOrUpdateFeedback(feedbackData) {
    try {
      // Vérifier si un feedback existe déjà pour cette demande et cet utilisateur
      const existingFeedbackQuery = query(
        collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK),
        where('workRequestId', '==', feedbackData.workRequestId),
        where('createdBy', '==', feedbackData.createdBy)
      );
      
      const querySnapshot = await getDocs(existingFeedbackQuery);
      
      // Préparer les données à enregistrer
      const dataToSave = {
        ...feedbackData,
        updatedAt: serverTimestamp()
      };
      
      let feedbackId;
      
      if (!querySnapshot.empty) {
        // Mettre à jour le feedback existant
        feedbackId = querySnapshot.docs[0].id;
        await updateDoc(doc(db, COLLECTIONS.WORK_REQUEST_FEEDBACK, feedbackId), dataToSave);
      } else {
        // Créer un nouveau feedback
        dataToSave.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK), dataToSave);
        feedbackId = docRef.id;
        
        // Mettre à jour la demande pour indiquer qu'un feedback a été donné
        await updateDoc(doc(db, COLLECTIONS.WORK_REQUESTS, feedbackData.workRequestId), {
          hasFeedback: true,
          updatedAt: serverTimestamp()
        });
        
        // Créer une notification pour le service technique
        await this.notifyTechnicalServiceAboutFeedback(feedbackData);
      }
      
      return feedbackId;
    } catch (error) {
      console.error('Erreur lors de la création/mise à jour du feedback:', error);
      throw error;
    }
  }
  
  /**
   * Récupère tous les feedbacks pour une demande de travaux spécifique
   * @param {string} workRequestId - ID de la demande de travaux
   * @returns {Promise<Array>} - Liste des feedbacks
   */
  static async getFeedbacksForWorkRequest(workRequestId) {
    try {
      const feedbackQuery = query(
        collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK),
        where('workRequestId', '==', workRequestId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(feedbackQuery);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des feedbacks:', error);
      throw error;
    }
  }
  
  /**
   * Récupère le feedback d'un utilisateur spécifique pour une demande
   * @param {string} workRequestId - ID de la demande de travaux
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object|null>} - Le feedback ou null si non trouvé
   */
  static async getUserFeedbackForWorkRequest(workRequestId, userId) {
    try {
      const feedbackQuery = query(
        collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK),
        where('workRequestId', '==', workRequestId),
        where('createdBy', '==', userId),
        limit(1)
      );
      
      const querySnapshot = await getDocs(feedbackQuery);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      return {
        id: querySnapshot.docs[0].id,
        ...querySnapshot.docs[0].data()
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du feedback utilisateur:', error);
      throw error;
    }
  }
  
  /**
   * Récupère les statistiques de feedback pour un service technique
   * @param {string} technicalServiceId - ID du service technique
   * @returns {Promise<Object>} - Statistiques des feedbacks
   */
  static async getFeedbackStatsForTechnicalService(technicalServiceId) {
    try {
      // Récupérer toutes les demandes traitées par ce service technique
      const workRequestsQuery = query(
        collection(db, COLLECTIONS.WORK_REQUESTS),
        where('technicalServiceId', '==', technicalServiceId),
        where('status', '==', 'Terminé')
      );
      
      const workRequestsSnapshot = await getDocs(workRequestsQuery);
      const workRequestIds = workRequestsSnapshot.docs.map(doc => doc.id);
      
      if (workRequestIds.length === 0) {
        return {
          averageRating: 0,
          totalFeedbacks: 0,
          ratingDistribution: {
            5: 0, 4: 0, 3: 0, 2: 0, 1: 0
          },
          detailsAverages: {
            timeliness: { excellent: 0, good: 0, average: 0, poor: 0 },
            quality: { excellent: 0, good: 0, average: 0, poor: 0 },
            communication: { excellent: 0, good: 0, average: 0, poor: 0 }
          }
        };
      }
      
      // Récupérer tous les feedbacks pour ces demandes
      const feedbacks = [];
      
      for (const workRequestId of workRequestIds) {
        const feedbackQuery = query(
          collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK),
          where('workRequestId', '==', workRequestId)
        );
        
        const feedbackSnapshot = await getDocs(feedbackQuery);
        feedbackSnapshot.forEach(doc => {
          feedbacks.push({
            id: doc.id,
            ...doc.data()
          });
        });
      }
      
      // Calculer les statistiques
      const totalFeedbacks = feedbacks.length;
      
      if (totalFeedbacks === 0) {
        return {
          averageRating: 0,
          totalFeedbacks: 0,
          ratingDistribution: {
            5: 0, 4: 0, 3: 0, 2: 0, 1: 0
          },
          detailsAverages: {
            timeliness: { excellent: 0, good: 0, average: 0, poor: 0 },
            quality: { excellent: 0, good: 0, average: 0, poor: 0 },
            communication: { excellent: 0, good: 0, average: 0, poor: 0 }
          }
        };
      }
      
      // Distribution des notes
      const ratingDistribution = {
        5: 0, 4: 0, 3: 0, 2: 0, 1: 0
      };
      
      // Compteurs pour les détails
      const detailsCount = {
        timeliness: { excellent: 0, good: 0, average: 0, poor: 0 },
        quality: { excellent: 0, good: 0, average: 0, poor: 0 },
        communication: { excellent: 0, good: 0, average: 0, poor: 0 }
      };
      
      let ratingSum = 0;
      
      feedbacks.forEach(feedback => {
        // Note globale
        const rating = feedback.rating || 0;
        ratingSum += rating;
        
        if (rating >= 1 && rating <= 5) {
          ratingDistribution[rating]++;
        }
        
        // Détails
        if (feedback.details) {
          if (feedback.details.timeliness) {
            detailsCount.timeliness[feedback.details.timeliness]++;
          }
          
          if (feedback.details.quality) {
            detailsCount.quality[feedback.details.quality]++;
          }
          
          if (feedback.details.communication) {
            detailsCount.communication[feedback.details.communication]++;
          }
        }
      });
      
      const averageRating = totalFeedbacks > 0 ? ratingSum / totalFeedbacks : 0;
      
      return {
        averageRating: parseFloat(averageRating.toFixed(1)),
        totalFeedbacks,
        ratingDistribution,
        detailsAverages: detailsCount
      };
    } catch (error) {
      console.error('Erreur lors du calcul des statistiques de feedback:', error);
      throw error;
    }
  }
  
  /**
   * Supprime un feedback
   * @param {string} feedbackId - ID du feedback à supprimer
   * @returns {Promise<void>}
   */
  static async deleteFeedback(feedbackId) {
    try {
      // Récupérer d'abord le feedback pour avoir l'ID de la demande
      const feedbackDoc = await getDoc(doc(db, COLLECTIONS.WORK_REQUEST_FEEDBACK, feedbackId));
      
      if (!feedbackDoc.exists()) {
        throw new Error('Feedback non trouvé');
      }
      
      const feedbackData = feedbackDoc.data();
      const workRequestId = feedbackData.workRequestId;
      
      // Supprimer le feedback
      await deleteDoc(doc(db, COLLECTIONS.WORK_REQUEST_FEEDBACK, feedbackId));
      
      // Vérifier s'il reste des feedbacks pour cette demande
      const remainingFeedbacksQuery = query(
        collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK),
        where('workRequestId', '==', workRequestId)
      );
      
      const remainingFeedbacksSnapshot = await getDocs(remainingFeedbacksQuery);
      
      // Si plus aucun feedback, mettre à jour la demande
      if (remainingFeedbacksSnapshot.empty) {
        await updateDoc(doc(db, COLLECTIONS.WORK_REQUESTS, workRequestId), {
          hasFeedback: false,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du feedback:', error);
      throw error;
    }
  }
  
  /**
   * Notifie le service technique qu'un feedback a été laissé
   * @param {Object} feedbackData - Les données du feedback
   * @returns {Promise<void>}
   */
  static async notifyTechnicalServiceAboutFeedback(feedbackData) {
    try {
      // Récupérer la demande pour obtenir l'ID du service technique
      const workRequestDoc = await getDoc(doc(db, COLLECTIONS.WORK_REQUESTS, feedbackData.workRequestId));
      
      if (!workRequestDoc.exists()) {
        console.warn('Demande non trouvée pour la notification de feedback');
        return;
      }
      
      const workRequestData = workRequestDoc.data();
      const technicalServiceId = workRequestData.technicalServiceId || workRequestData.assignedServiceId;
      
      if (!technicalServiceId) {
        console.warn('Service technique non identifié pour la notification de feedback');
        return;
      }
      
      // Créer une notification pour le service technique
      await NotificationService.createFeedbackNotification(
        workRequestData,
        feedbackData.rating,
        feedbackData.createdByName || 'Un demandeur',
        technicalServiceId
      );
    } catch (error) {
      console.error('Erreur lors de la notification du service technique:', error);
      // Ne pas propager l'erreur pour ne pas bloquer le processus principal
    }
  }

  /**
   * Récupère tous les feedbacks créés par un utilisateur spécifique
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Array>} - Liste des feedbacks avec les détails des demandes associées
   */
  static async getUserFeedbacks(userId) {
    try {
      // Récupérer tous les feedbacks créés par cet utilisateur
      const feedbackQuery = query(
        collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK),
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(feedbackQuery);
      
      if (querySnapshot.empty) {
        return [];
      }
      
      // Récupérer les détails complets pour chaque feedback
      const feedbacksWithDetails = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          const feedbackData = {
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || null,
            updatedAt: doc.data().updatedAt?.toDate() || null
          };
          
          // Récupérer les détails de la demande associée
          if (feedbackData.workRequestId) {
            const workRequestDoc = await getDoc(doc(db, COLLECTIONS.WORK_REQUESTS, feedbackData.workRequestId));
            
            if (workRequestDoc.exists()) {
              const workRequestData = workRequestDoc.data();
              feedbackData.workRequestTitle = workRequestData.title || `Demande #${feedbackData.workRequestId.substring(0, 8)}`;
              
              // Récupérer le nom du service technique
              if (workRequestData.technicalServiceId || workRequestData.assignedServiceId) {
                try {
                  const serviceId = workRequestData.technicalServiceId || workRequestData.assignedServiceId;
                  const serviceDocRef = doc(db, COLLECTIONS.SERVICES, serviceId);
                  const serviceDoc = await getDoc(serviceDocRef);
                  
                  if (serviceDoc.exists()) {
                    feedbackData.technicalServiceName = serviceDoc.data().name || 'Service non identifié';
                  } else {
                    feedbackData.technicalServiceName = 'Service non trouvé';
                  }
                } catch (serviceError) {
                  console.error('Erreur lors de la récupération du service:', serviceError);
                  feedbackData.technicalServiceName = 'Service technique';
                }
              }
            }
          }
          
          // Extraire les critères spécifiques des détails
          if (feedbackData.details) {
            feedbackData.timeliness = feedbackData.details.timeliness || 'not_rated';
            feedbackData.quality = feedbackData.details.quality || 'not_rated';
            feedbackData.communication = feedbackData.details.communication || 'not_rated';
          }
          
          return feedbackData;
        })
      );
      
      return feedbacksWithDetails;
    } catch (error) {
      console.error('Erreur lors de la récupération des feedbacks utilisateur:', error);
      throw error;
    }
  }
}

export default FeedbackService;
